import {
  createContext,
  type FC,
  type ReactElement,
  useContext,
  useMemo,
  useState,
} from 'react'

import { type OpenableControls } from '../../../generic-hooks'

type AccountManagementDialogContextValue = {
  dialogControls: OpenableControls
  selectedContent: ReactElement | null
  setSelectedContent: (content: ReactElement) => void
}

const AccountManagementDialogContext = createContext<
  AccountManagementDialogContextValue | undefined
>(undefined)

export const AccountManagementDialogProvider: FC<{
  children: ReactElement
  dialogControls: OpenableControls
}> = ({ children, dialogControls }) => {
  const [selectedContent, setSelectedContent] = useState<ReactElement | null>(
    null,
  )

  const memoizedValue: AccountManagementDialogContextValue = useMemo(
    () => ({
      dialogControls: {
        ...dialogControls,
        handleClose: (): void => {
          setSelectedContent(null)
          dialogControls.handleClose()
        },
      },
      selectedContent,
      setSelectedContent,
    }),
    [dialogControls, selectedContent],
  )

  return (
    <AccountManagementDialogContext.Provider value={memoizedValue}>
      {children}
    </AccountManagementDialogContext.Provider>
  )
}

export const useAccountManagementDialogContext =
  (): AccountManagementDialogContextValue => {
    const context = useContext(AccountManagementDialogContext)
    if (context === undefined) {
      throw new Error(
        'useAccountManagementDialogContext must be used within a AccountManagementDialogProvider',
      )
    }

    return context
  }
